import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useEffectEvent } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import {
  reatomUpdateTradeSettings,
  tradeSettingsModel,
} from '@root/features/trade/trade-settings'
import { activePresetIndexAtom } from '@root/features/trade/trade-settings/atoms'
import { TradeSettingsPresetSelector } from '@root/features/trade/trade-settings/ui/common/trade-settings-preset-selector'
import { FadeOverflow } from '@root/shared/ui-kit'

import {
  createDefaultValues,
  formValuesFromSettings,
  tradeSettingsFromValues,
} from '../../common'

import { TradeSettingsFormAction } from './trade-settings-form-action'
import { TradeSettingsFormSol } from './trade-settings-form-sol'
import { TradeSettingsQuickBuySections } from './trade-settings-quick-buy-sections'

import type { TradeSettingsValues } from '../../common'
import type { TradeSettings } from '@root/entities/trade-settings'
import type { ClassNameProps } from '@root/shared/react'

export type TradeSettingsFormProps = {
  /**
   * If shouldSync is false, settings will be passed
   */
  onSaved?: (settings: TradeSettings.TradeSettings | undefined) => void
} & ClassNameProps

export const TradeSettingsPresetForm: React.FC<TradeSettingsFormProps> = ({
  onSaved,
  className,
}) => {
  const [presetIndex] = useAtom(activePresetIndexAtom)
  const [tradeSettingsPresetState] = useAtom(
    tradeSettingsModel.networkSettingsPresetState,
  )
  const [tradeSettingsState] = useAtom(tradeSettingsModel.networkSettingsState)
  const [defaultSettingsState] = useAtom(
    tradeSettingsModel.networkDefaultSettingsState,
  )

  const [network] = useAtom(selectedNetworkAtom)

  const initialValues = useMemo(() => {
    if (tradeSettingsState.value) {
      return formValuesFromSettings(tradeSettingsState.value)
    }
    if (defaultSettingsState.value) {
      return formValuesFromSettings(defaultSettingsState.value)
    }

    return createDefaultValues()
  }, [defaultSettingsState.value, tradeSettingsState.value])

  const form = useForm<TradeSettingsValues>({
    defaultValues: initialValues,
    mode: 'all',
  })

  const updateModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomUpdateTradeSettings({ network, presetIndex: presetIndex }),
    [network, presetIndex],
  )

  const updateTradeSettings = useAction(updateModel.execute)
  const [requestState] = useAtom(updateModel)

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onSaved?.(undefined)
    }
  }, [requestState, onSaved])

  const handleSave = form.handleSubmit((values) => {
    const tradeSettings = tradeSettingsFromValues(network, values)

    updateTradeSettings(tradeSettings)
  })

  const handleReset = () => {
    if (!AsyncStateN.isSuccess(defaultSettingsState)) {
      return
    }

    const settings = defaultSettingsState.value
    form.reset(formValuesFromSettings(settings))
  }

  const resetForm = useEffectEvent(() => {
    form.reset(initialValues)
  })

  useEffect(() => {
    resetForm()
  }, [presetIndex])

  const renderForm = () => {
    // Support for solana only now
    if (network !== Network.Solana) {
      return undefined
    }

    return <TradeSettingsFormSol preset={presetIndex} />
  }

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSave} className={className}>
        <Overflow
          mode="always"
          variant="vertical"
          blockEnd={14}
          blockStart={14}
        >
          <PresetContainer>
            <TradeSettingsPresetSelector />
          </PresetContainer>
          {tradeSettingsPresetState.value ? (
            <PresetTitle>
              <span>{tradeSettingsPresetState.value.emoji}</span>
              <span>{tradeSettingsPresetState.value.name}</span>
            </PresetTitle>
          ) : undefined}
          {renderForm()}
          <Sections />
        </Overflow>

        <TradeSettingsFormAction
          requestState={requestState}
          onReset={handleReset}
        />
      </Form>
    </FormProvider>
  )
}

const Form = styled.form`
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: auto;
`

const Overflow = styled(FadeOverflow)`
  && {
    overflow-y: auto;
  }
  display: grid;
  align-content: start;

  padding-inline: 10px;
  padding-block: 0 10px;
`

const PresetContainer = styled.div`
  padding-block: 7px;
  border-bottom: 1px solid var(--backgroundA-3);
`

const PresetTitle = styled.div`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 6px;
  margin-block: 12px;
`

const Sections = styled(TradeSettingsQuickBuySections)`
  margin-block: 8px;
`
